import { cn, typographyVariants } from '@seen/ui'
import Document from '@tiptap/extension-document'
import Heading from '@tiptap/extension-heading'
import Placeholder, {
  type PlaceholderOptions,
} from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import { EditorContent, useEditor } from '@tiptap/react'
import { cva } from 'class-variance-authority'
import { forwardRef, useImperativeHandle } from 'react'
import { tiptapVariants } from '#src/components/core/tiptapVariants.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSyncEditorContent } from '#src/hooks/useSyncEditorContent.ts'

export interface H1EditorProps {
  value: string
  onChange: (value: string) => any
  placeholder?: PlaceholderOptions['placeholder']
  loading?: boolean
  onFocus?: (event: FocusEvent) => any
  onBlur?: (event: FocusEvent) => any
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  disabled?: boolean
}

export type H1EditorRef = {
  focus: () => void
  blur: () => void
}

export const H1Editor = forwardRef<H1EditorRef, H1EditorProps>(
  ({ loading, disabled, ...rest }, ref) => {
    const isReadOnly = useIsReadOnly()
    const isDisabled = disabled || isReadOnly
    const isEditable = !loading && !isDisabled
    return (
      <H1EditorInner
        ref={ref}
        key={isEditable ? 'editable' : 'non-editable'} // There's a bug where if editable changes, the extensions (i.e. ChoiceNode) don't update accordingly, so we force a re-initialization of the editor.
        loading={loading}
        disabled={isDisabled}
        {...rest}
      />
    )
  }
)
H1Editor.displayName = 'H1Editor'

const H1EditorInner = forwardRef<H1EditorRef, H1EditorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      loading,
      disabled,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    const isEditable = !loading && !disabled
    const attributes: Record<string, string> = {}
    if (id) {
      attributes.id = id
    }
    if (ariaDescribedBy) {
      attributes['aria-describedby'] = ariaDescribedBy
    }
    if (ariaInvalid) {
      attributes['aria-invalid'] = ariaInvalid.toString()
    }
    attributes.class = cn(
      variants({
        loading,
        disabled,
      })
    )
    const editor = useEditor({
      // Defer view mount to after first commit so consumers reading editor.view don't fire before it's attached.
      immediatelyRender: false,
      // Re-render on every transaction so toolbar / selection-driven UI stays in sync with editor state.
      shouldRerenderOnTransaction: true,
      extensions: [
        Document,
        Text,
        Heading.configure({ levels: [1] }),
        Placeholder.configure({ placeholder }),
      ],
      content: value,
      onUpdate: ({ editor }) => {
        onChange(editor.getText())
      },
      onFocus: (props) => {
        onFocus?.(props.event)
      },
      onBlur: (props) => {
        onBlur?.(props.event)
      },
      editorProps: {
        attributes,
        handleDOMEvents: {
          keydown: (_, event) => {
            // Disable meta+enter. We often use it for form submission.
            if (event.metaKey && event.key === 'Enter') {
              return true
            }
            return false
          },
        },
      },
      editable: isEditable,
      // enableContentCheck: true,
      // onContentError: handleTiptapContentError,
    })

    useSyncEditorContent(editor, value)

    useImperativeHandle(ref, () => ({
      focus() {
        editor?.commands.focus()
      },
      blur() {
        editor?.commands.blur()
      },
    }))

    return <EditorContent editor={editor} />
  }
)
H1EditorInner.displayName = 'H1EditorInner'

const variants = cva(
  cn(
    typographyVariants({ variant: 'h1' }),
    tiptapVariants({
      noHeadings: true,
      noLists: true,
      noPlaceholder: true, // We handle the h1 placeholder ourselves below.
    }),
    'hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden',
    '[&_h1.is-editor-empty]:first:before:pointer-events-none [&_h1.is-editor-empty]:first:before:float-left [&_h1.is-editor-empty]:first:before:h-0 [&_h1.is-editor-empty]:first:before:opacity-50 [&_h1.is-editor-empty]:first:before:content-[attr(data-placeholder)]' // https://tiptap.dev/docs/editor/extensions/functionality/placeholder#placeholder
  ),
  {
    variants: {
      loading: {
        true: 'text-transparent transition-none after:absolute after:inset-0 after:z-10 after:animate-pulse after:rounded-md after:backdrop-brightness-loading',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: '',
      },
    },
    defaultVariants: {
      loading: false,
      disabled: false,
    },
  }
)
